import { useEffect, useState } from "react";

// PrimeReact Components
import { Card } from "primereact/card";
import { Button } from "primereact/button";

import InputTextOnly from "../../controls/InputTextOnly";
import SelectMenu from "../../controls/SelectMenu";
import TextArea from "../../controls/TextArea";
import {
  convertToDefEventPara,
  sellingUnitOfMeasureOptions,
  sellingUnitOfMeasureToSelectOption,
} from "../../../shared/utility";
import ImportMediaDropzone from "../../importMedia/ImportMediaDropzone";
import DollarInputText from "../../controls/DollarInputText";
import InputNumberOnly from "../../controls/InputNumberOnly";
import EmptyModal from "../../modals/EmptyModal";

// TypeScript interfaces
interface Product {
  id?: number;
  name: string;
  sku: string;
  barcode: string;
  unit_cost?: number | string;
  unit_price: number;
  stock: number;
  low_stock_threshold: number;
  taxable: number;
  quickbooks_id: string;
  selling_unit_of_measure: string;
  description: string;
  primary_category: Option | null;
  sub_category: Option | null;
  image1?: string;
  image2?: string;
  projectTags?: string[];
}

interface Option {
  value: number | string;
  label: string;
}

interface FormValues extends Partial<Product> {
  projectTags?: string[];
}

interface ProductDetailsFormProps {
  product: Product | null;
  primaryCategoryOptions: Option[];
  subCategoryOptions: Option[];
  getSubCategoriesByPrimary: (primaryCategoryId: number) => void;
  setValues: (values: FormValues | ((prevValues: FormValues) => FormValues)) => void;
  values: FormValues;
  handleSubmit: (e: React.FormEvent) => void;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { target: { name: string; value: any } }) => void;
  deleteProductImage: (imageType: 'image1' | 'image2') => Promise<boolean>;
}

const ProductDetailsForm: React.FC<ProductDetailsFormProps> = (props) => {
  const {
    product,
    primaryCategoryOptions,
    subCategoryOptions,
    getSubCategoriesByPrimary,
    setValues,
    values,
    handleSubmit,
    handleInputChange,
    deleteProductImage,
  } = props;

  const onPrimaryCategoryChanged = (newValue: Option | null) => {
    setValues({
      ...values,
      primary_category: newValue,
      sub_category: null,
    });
    
    if (newValue != null) {
      getSubCategoriesByPrimary(Number(newValue.value));
    }
  };

  useEffect(() => {
    if (product) {
      setValues(product);
    }
  }, [product, setValues]);

  const [image1, setImage1] = useState<string | null>(null);
  const [image2, setImage2] = useState<string | null>(null);
  const [selectedProjectTags, setSelectedProjectTags] = useState<string[]>([]);
  
  // Modal states
  const [editImageModal, setEditImageModal] = useState<{
    open: boolean;
    imageType: 'image1' | 'image2' | null;
  }>({
    open: false,
    imageType: null,
  });

  // Update values with selected project tags
  useEffect(() => {
    setValues(prevValues => ({
      ...prevValues,
      projectTags: selectedProjectTags
    }));
  }, [selectedProjectTags, setValues]);

  // Handle image delete
  const handleImageDelete = async (imageType: 'image1' | 'image2') => {
    if (!product?.id) return;
    
    try {
      const success = await deleteProductImage(imageType);
      if (success) {
        // Update the form values to remove the image
        setValues(prevValues => ({
          ...prevValues,
          [imageType]: null,
        }));
        
        // Clear the local state
        if (imageType === 'image1') {
          setImage1(null);
        } else {
          setImage2(null);
        }
      }
    } catch (error) {
      console.error('Error deleting image:', error);
    }
  };

  // Handle image edit modal
  const handleImageEdit = (imageType: 'image1' | 'image2') => {
    setEditImageModal({
      open: true,
      imageType,
    });
  };

  // Handle image upload from modal
  const handleImageUpload = (imageUrl: string, imageType: 'image1' | 'image2') => {
    setValues(prevValues => ({
      ...prevValues,
      [imageType]: imageUrl,
    }));
    
    if (imageType === 'image1') {
      setImage1(imageUrl);
    } else {
      setImage2(imageUrl);
    }
    
    setEditImageModal({
      open: false,
      imageType: null,
    });
  };

  // Project tags options
  const projectTagsOptions: Option[] = [
    { label: "In Stock", value: "in_stock" },
    { label: "Out of Stock", value: "out_of_stock" },
    { label: "Featured", value: "featured" },
    { label: "Best Seller", value: "best_seller" },
    { label: "New Arrival", value: "new_arrival" },
    { label: "Sale Item", value: "sale_item" }
  ];

  return (
    <div>
      <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-6 gap-6">
        {/* Product Image Section - 2/5 width */}
        <div className="col-span-2">
          <Card className="apollo-card h-full" style={{ backgroundColor: 'white' }}>
            <div className="p-2">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Product Image</h3>
            <div className="space-y-6">
              {/* Image 1 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Image 1
                </label>
                <div className="space-y-3">
                  {image1 ? (
                    <div className="relative">
                      <img 
                        src={image1} 
                        alt="Product Image 1" 
                        className="w-full h-48 object-cover rounded-lg border border-gray-200"
                      />
                      <div className="absolute top-2 right-2 flex gap-2">
                        <Button
                          type="button"
                          size="small"
                          className="px-3 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded"
                          onClick={() => handleImageEdit('image1')}
                        >
                          Edit
                        </Button>
                        <Button
                          type="button"
                          size="small"
                          className="px-3 py-1 text-xs bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 rounded"
                          onClick={() => handleImageDelete('image1')}
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  ) : values.image1 ? (
                    <div className="relative">
                      <img
                        src={values.image1}
                        alt="Product Image 1"
                        className="w-full h-48 object-cover rounded-lg border border-gray-200"
                      />
                      <div className="absolute top-2 right-2 flex gap-2">
                        <Button
                          type="button"
                          size="small"
                          className="px-3 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded"
                          onClick={() => handleImageEdit('image1')}
                        >
                          Edit
                        </Button>
                        <Button
                          type="button"
                          size="small"
                          className="px-3 py-1 text-xs bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 rounded"
                          onClick={() => handleImageDelete('image1')}
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                      <ImportMediaDropzone
                        fileName="image1"
                        handleInputChange={handleInputChange}
                        setUploadedImage={setImage1}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Image 2 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Image 2
                </label>
                <div className="space-y-3">
                  {image2 ? (
                    <div className="relative">
                      <img 
                        src={image2} 
                        alt="Product Image 2" 
                        className="w-full h-48 object-cover rounded-lg border border-gray-200"
                      />
                      <div className="absolute top-2 right-2 flex gap-2">
                        <Button
                          type="button"
                          size="small"
                          className="px-3 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded"
                          onClick={() => handleImageEdit('image2')}
                        >
                          Edit
                        </Button>
                        <Button
                          type="button"
                          size="small"
                          className="px-3 py-1 text-xs bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 rounded"
                          onClick={() => handleImageDelete('image2')}
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  ) :  values.image2 ? (
                    <div className="relative">
                      <img
                        src={values.image2}
                        alt="Product Image 2"
                        className="w-full h-48 object-cover rounded-lg border border-gray-200"
                      />
                      <div className="absolute top-2 right-2 flex gap-2">
                        <Button
                          type="button"
                          size="small"
                          className="px-3 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded"
                          onClick={() => handleImageEdit('image2')}
                        >
                          Edit
                        </Button>
                        <Button
                          type="button"
                          size="small"
                          className="px-3 py-1 text-xs bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 rounded"
                          onClick={() => handleImageDelete('image2')}
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                      <ImportMediaDropzone
                        fileName="image2"
                        handleInputChange={handleInputChange}
                        setUploadedImage={setImage2}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Card>
        </div>

        {/* Product Information and Pricing Section - 3/5 width */}
        <div className="col-span-4 space-y-6">
          {/* Product Information Section */}
          <Card className="apollo-card" style={{ backgroundColor: 'white' }}>
            <div className="p-2">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Product Information</h3>
              <div className="space-y-6">
                {/* Basic Info Row 1 */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <InputTextOnly
                    label="Name"
                    name="name"
                    onChange={handleInputChange}
                    value={values.name || ''}
                    labelOn={true}
                    className="w-full"
                  />
                  <InputTextOnly
                    label="Sku"
                    name="sku"
                    onChange={handleInputChange}
                    value={values.sku || ''}
                    labelOn={true}
                    className="w-full"
                  />
                   <InputTextOnly
                    label="Barcode"
                    name="barcode"
                    onChange={handleInputChange}
                    value={values.barcode || ''}
                    labelOn={true}
                    className="w-full"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                  <DollarInputText
                    label="Unit Cost"
                    name="unit_cost"
                    onChange={handleInputChange}
                    value={values.unit_cost}
                    labelOn={true}
                    className="w-full"
                  />
                  <DollarInputText
                    label="Unit Price"
                    name="unit_price"
                    onChange={handleInputChange}
                    value={values.unit_price}
                    labelOn={true}
                    className="w-full"
                  />
                    <InputNumberOnly
                    label="Inventory on hand"
                    name="stock"
                    onChange={handleInputChange}
                    value={values.stock}
                    labelOn={true}
                    className="w-full"
                  />
                  <InputNumberOnly
                    label="Low Stock Threshold"
                    name="low_stock_threshold"
                    onChange={handleInputChange}
                    value={values.low_stock_threshold}
                    labelOn={true}
                    className="w-full"
                  />
                  <InputTextOnly
                   label="Taxable"
                   name="taxable"
                   onChange={handleInputChange}
                   value={values.taxable === 1 ? "Yes":"No"}
                   labelOn={true}
                   disabled={true}
                   className="w-full"
                 />
                  <InputTextOnly
                   label="External Id"
                   name="quickbooks_id"
                   onChange={handleInputChange}
                   value={values.quickbooks_id}
                   labelOn={true}
                   disabled={true}
                   className="w-full"
                 />
                 <SelectMenu
                    handleInputChange={(e: {
                      target: { name: string; value: Option | null };
                    }) => {
                      const v = e.target.value;
                      const next =
                        v == null
                          ? ""
                          : typeof v === "object" && "value" in v
                            ? v.value
                            : v;
                      handleInputChange(
                        convertToDefEventPara("selling_unit_of_measure", next)
                      );
                    }}
                    name="selling_unit_of_measure"
                    value={sellingUnitOfMeasureToSelectOption(
                      values.selling_unit_of_measure
                    )}
                    options={sellingUnitOfMeasureOptions}
                    title={"Unit of measure"}
                  />
                </div>

                {/* Description */}
                <div>
                  <TextArea
                    className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    label="Description"
                    labelOn={true}
                    name="description"
                    value={values.description || ''}
                    onChange={handleInputChange}
                    rows="4"
                  />
                </div>

                {/* Categories */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectMenu
                    handleInputChange={(e: React.ChangeEvent<HTMLSelectElement> & { target: { value: Option } }) => onPrimaryCategoryChanged(e.target.value)}
                    name="primary_category"
                    value={values.primary_category}
                    options={primaryCategoryOptions}
                    title={"Category"}
                  />
                  {values.primary_category && (
                    <SelectMenu
                      handleInputChange={(e: React.ChangeEvent<HTMLSelectElement> & { target: { value: Option } }) =>
                        handleInputChange(
                          convertToDefEventPara("sub_category", e.target.value)
                        )
                      }
                      name="sub_category"
                      value={values.sub_category}
                      options={subCategoryOptions}
                      title={"Sub Category"}
                    />
                  )}
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end pt-6 border-t border-gray-200">
        <Button
          type="submit"
          className="px-6 py-3 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-medium rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
        >
          {product === null
            ? "Loading..."
            : values.id
            ? "Save Changes"
            : "Create Product"}
        </Button>
      </div>
    </form>

    {/* Edit Image Modal */}
    <EmptyModal
      childComponent={
        editImageModal.imageType ? (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Edit {editImageModal.imageType === 'image1' ? 'Image 1' : 'Image 2'}
              </h3>
              <p className="text-gray-600">Upload a new image or drag and drop</p>
            </div>
            
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <ImportMediaDropzone
                fileName={editImageModal.imageType}
                handleInputChange={(e: any) => {
                  // Handle the upload and get the image URL
                  // This would typically come from the dropzone component
                  const imageUrl = e.target.value; // This might need adjustment based on your dropzone implementation
                  handleImageUpload(imageUrl, editImageModal.imageType!);
                }}
                setUploadedImage={(imageUrl: string) => {
                  handleImageUpload(imageUrl, editImageModal.imageType!);
                }}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <Button
                type="button"
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-all duration-200"
                onClick={() => setEditImageModal({ open: false, imageType: null })}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : null
      }
      open={editImageModal.open}
      setOpen={(modalState: any) => setEditImageModal({ open: modalState.open, imageType: null })}
    />
    </div>
  );
};

export default ProductDetailsForm;
